import { directionalProperty } from 'polished';
import { css } from 'styled-components';
import { media } from '..';
import { globalSpacing, scales } from '../common/spacing';
const AUTO_SCALE_VALUE = 'auto';
const ALL_BREAKPOINTS_VALUE = '_';
/**
 * Returns a scaled unit (or 'auto'), based on the passed scale.
 * @example
 *
 * getScaledUnit('normal'); // -> 1rem
 * getScaledUnit('large', true); // -> 1.563rem !important
 *
 * @param scale is the scale to get the unit for
 * @param withImportant is a bool that, if true, adds '!important' to the unit
 */
const getScaledUnit = (scale, force) => {
    if (scale === AUTO_SCALE_VALUE) {
        return `${AUTO_SCALE_VALUE}${force === true ? ' !important' : ''}`;
    }
    // Apply the scale to the global spacing and return the resulting value in rems
    const calculatedScale = globalSpacing * (!scale ? scales.normal : scales[scale]);
    const scaledUnit = !calculatedScale ? 0 : `${calculatedScale}rem`;
    return `${scaledUnit}${force === true ? ' !important' : ''}`;
};
/**
 * Returns an object with methods used to add spacing rules to a styled component definition.
 * @param attr is either 'margin' or 'padding' and indicates for which attribute the helper will be made
 */
const makeSpacingHelper = (attr) => {
    const methods = {
        top: (scale, force) => css `
      ${attr}-top: ${getScaledUnit(scale, force)};
    `,
        right: (scale, force) => css `
      ${attr}-right: ${getScaledUnit(scale, force)};
    `,
        bottom: (scale, force) => css `
      ${attr}-bottom: ${getScaledUnit(scale, force)};
    `,
        left: (scale, force) => css `
      ${attr}-left: ${getScaledUnit(scale, force)};
    `,
        /**
         * Applies the provided scale to the horizontal axis
         * @param scale is a valid Scale, or 'auto. If no scale is provided, scale 'normal' is used
         */
        horizontal: (scale, force) => css `
      ${attr}-left: ${getScaledUnit(scale, force)};
      ${attr}-right: ${getScaledUnit(scale, force)};
    `,
        /**
         * Applies the provided scale to the vertical axis
         * @param scale is a valid Scale, or AutoScale. If no scale is provided, scale 'normal' is used
         */
        vertical: (scale, force) => css `
      ${attr}-top: ${getScaledUnit(scale, force)};
      ${attr}-bottom: ${getScaledUnit(scale, force)};
    `,
        /**
         * Mimics native CSS syntax and thus supports up to five arguments (four unit values,
         * and a force argument)
         * @param values are up to four Scale values, and an optional 'force' boolean.
         */
        all: (...values) => {
            const lastArg = values[values.length - 1];
            let force = false;
            let allValues = [...values];
            // If no values are provided, then fall back to the default 'normal' as the single value
            if (!allValues.length) {
                allValues = ['normal'];
            }
            // If the last argument is a boolean, then remove it from the values and assign it to
            // the 'force' argument
            if (typeof lastArg === 'boolean') {
                allValues = values.slice(0, -1);
                // Assign the 'force' argument to the last value
                force = lastArg;
            }
            return css `
        ${directionalProperty(attr, ...allValues.map((scale) => `${getScaledUnit(scale, force)}`))}
      `;
        },
    };
    return methods;
};
/**
 * Returns an integer representing the named scale supplied to the method
 * @param scale is the named scale to get the value for, e.g. 'large', which is equal to 1.563. If left empty,
 * the 'normal' value (which is equal to 1) is returned
 */
const scale = (_scale) => (!_scale ? scales.normal : scales[_scale]);
/**
 * Add margin attributes to be used in a component
 *
 * @example
 * const StyledDiv = styled.div`
 *   ${margin.bottom('small')}
 * `
 */
const margin = makeSpacingHelper('margin');
/**
 * Add padding attributes to be used in a component
 *
 * @example
 * const StyledDiv = styled.div`
 *   ${padding.vertical('small')}
 * `
 */
const padding = makeSpacingHelper('padding');
/**
 * Exposes spacing props to a component
 *
 * @example
 *
 * // First add it to the component
 * const StyledDiv = styled.h2`
 *   ${spacing}
 * `;
 *
 * // Then, use the props in the component
 * <StyledDiv marginBottom="small" paddingTop="large" marginRight={{_: 'tiny', sm: 'small', md: 'normal', lg: 'large'}}>...</StyledDiv>
 *
 * @param props are the props that will be exposed to the component
 */
const spacing = (props) => {
    const spacingCss = [];
    const keys = ['top', 'bottom', 'left', 'right', 'horizontal', 'vertical', 'all'];
    const attrs = ['margin', 'padding'];
    // Assigns the two mixins to an object with values so we can easily call them in the loop below
    const attrMethods = { margin, padding };
    attrs.forEach((attr) => {
        keys.forEach((key) => {
            // Combines the attribute and key into a prop, e.g. `margin` and `bottom` becomes `marginBottom`
            const propName = `${attr}${key.replace(/^\w/, (s) => s.toUpperCase())}`;
            const propValue = props[propName];
            // Don't attempt to render if the value is undefined
            if (!propValue)
                return;
            // Handle value when it's passed as an object
            if (typeof propValue === 'object') {
                Object.keys(propValue).forEach((breakpoint) => {
                    const breakpointValue = propValue[breakpoint];
                    if (breakpoint === ALL_BREAKPOINTS_VALUE) {
                        spacingCss.push(attrMethods[attr][key](breakpointValue));
                    }
                    else {
                        spacingCss.push(media.up(breakpoint) `${attrMethods[attr][key](breakpointValue)}`);
                    }
                });
                return;
            }
            const isForce = true;
            // Add the mixin value to the results
            // `force` paramater with value of `true` is passed in order to override the css values of class that is applied by `as` prop of  `Heading` component.
            // Otherwise, spacing props like `marginBottom` or `paddingLeft` gets invalid.
            spacingCss.push(attrMethods[attr][key](propValue, isForce));
        });
    });
    return spacingCss;
};
export { margin, padding, spacing, scale, getScaledUnit };
//# sourceMappingURL=spacing.js.map